import * as React from 'react';

import {useCSS} from '@pinecast/styles';
import {defineMessages, useIntl} from '@pinecast/i18n';
import {MOBILE} from './mobile';

const messages = defineMessages({
  label: {
    id: 'pl-spa.SeekBar.label',
    description: 'Label for the player seek bar',
    defaultMessage: 'Audio timeline',
  },
  valueText: {
    id: 'pl-spa.SeekBar.valueText',
    description:
      'Value text for the player seek bar. {seconds} is the number of seconds through a track the seek bar is displaying',
    defaultMessage: '{seconds} seconds',
  },
});

type Props = {
  audio: HTMLAudioElement;
  providedDuration: number;
  triggerLoad: () => void;
};

const SeekBar = ({audio, providedDuration, triggerLoad}: Props) => {
  const css = useCSS();
  const intl = useIntl();

  const seekBar = React.useRef<HTMLDivElement | null>(null);
  const seekHandle = React.useRef<HTMLDivElement | null>(null);

  const [duration, setDuration] = React.useState(
    audio.duration || providedDuration || 0,
  );
  const durationRef = React.useRef(duration);
  const [transitionDuration, setTransitionDuration] = React.useState(0.1);
  const lastTimestamp = React.useRef(Date.now());

  const dragging = React.useRef<boolean>(false);

  const setPosition = React.useCallback(
    (position: number) => {
      seekHandle.current!.style.left = `${Math.round(
        (position / duration) * 100,
      )}%`;
      seekHandle.current!.setAttribute('aria-valuenow', String(position));
      seekHandle.current!.setAttribute(
        'aria-valuetext',
        intl.formatMessage(messages.valueText, {
          seconds: Math.round(position),
        }),
      );
    },
    [duration, intl],
  );

  React.useEffect(() => {
    durationRef.current = audio.duration || providedDuration;
    setDuration(durationRef.current || 0);
    const onDurationChange = () => {
      setDuration(audio.duration);
      durationRef.current = audio.duration;
    };
    const onTimeUpdate = () => {
      setPosition(audio.currentTime);
      const now = Date.now();
      const delta = (now - lastTimestamp.current + 100) / 1000;
      setTransitionDuration(Math.min(1, delta));
      lastTimestamp.current = now;
    };
    audio.addEventListener('durationchange', onDurationChange);
    audio.addEventListener('timeupdate', onTimeUpdate);

    let rect: DOMRect | null = null;
    let paused = audio.paused;
    console.log(`Audio is paused: ${paused}`);

    const mouseDownHandler = (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      dragging.current = true;
      rect = seekBar.current!.getClientRects()[0];

      triggerLoad();
      if (durationRef.current) {
        const percent = Math.max(
          Math.min((e.clientX - rect.left) / rect.width, 1),
          0,
        );
        setPosition((audio.currentTime = percent * durationRef.current));
        lastTimestamp.current = Date.now();
      }
      paused = audio.paused;
      console.log(`Audio is paused on mousedown: ${paused}`);
      if (!paused) {
        audio.pause();
      }
    };

    const currentSeekbar = seekBar.current!;
    currentSeekbar.addEventListener('mousedown', mouseDownHandler, {
      capture: true,
    });

    const endDragging = (e: MouseEvent) => {
      if (!dragging.current) {
        return;
      }
      dragging.current = false;
      const percent = Math.min((e.clientX - rect!.left) / rect!.width, 1);
      setPosition((audio.currentTime = percent * durationRef.current));
      lastTimestamp.current = Date.now();
      console.log(`Audio is paused on end drag: ${paused}`);
      if (!paused) {
        audio.play();
      }
    };
    const mouseMove = (e: MouseEvent) => {
      if (!dragging.current) {
        return;
      }
      const percent = Math.max(
        Math.min((e.clientX - rect!.left) / rect!.width, 1),
        0,
      );
      setPosition((audio.currentTime = percent * durationRef.current));
      lastTimestamp.current = Date.now();
    };
    document.body.addEventListener('mouseup', endDragging);
    document.body.addEventListener('mouseleave', endDragging);
    document.body.addEventListener('mousemove', mouseMove);

    return () => {
      currentSeekbar.removeEventListener('mousedown', mouseDownHandler, {
        capture: true,
      });

      audio.removeEventListener('durationchange', onDurationChange);
      audio.removeEventListener('timeupdate', onTimeUpdate);

      document.body.removeEventListener('mouseup', endDragging);
      document.body.removeEventListener('mouseleave', endDragging);
      document.body.removeEventListener('mousemove', mouseMove);

      setPosition(0);
      dragging.current = false;
    };
  }, [audio, providedDuration, setPosition, triggerLoad]);

  const keyUp = React.useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      if (e.keyCode === 37) {
        // left
        if (audio.src) {
          setPosition((audio.currentTime -= 4));
          lastTimestamp.current = Date.now();
        }
      } else if (e.keyCode === 39) {
        // right
        if (audio.src) {
          setPosition((audio.currentTime += 4));
          lastTimestamp.current = Date.now();
        }
      } else if (e.keyCode === 32) {
        if (!audio || audio.paused) {
          triggerLoad();
          audio.play();
        } else {
          audio.pause();
        }
      }
    },
    [audio, setPosition, triggerLoad],
  );

  return (
    <div
      onKeyUp={keyUp}
      className={css({
        contain: 'size layout',
        height: '24px',
        padding: '8px 12px',
        [MOBILE]: {padding: '6px 8px', height: '20px'},
      })}
      ref={seekBar}
    >
      <div
        className={css({
          borderRadius: '4px',
          background: 'rgba(127, 127, 127, 0.2)',
          contain: 'size layout',
          height: '8px',
        })}
      >
        <div
          ref={seekHandle}
          className={css({
            backgroundColor: '#fff',
            border: '1px solid rgba(0, 0, 0, 0.3)',
            borderRadius: '12px',
            boxShadow: '0 2px 2px rgba(0, 0, 0, 0.1), 0 0 0 0 transparent',
            contain: 'size layout paint',
            transform: 'translateX(-12px)',
            left: 0,
            top: '-8px',
            position: 'relative',
            transition: `left ${
              Math.round(transitionDuration * 10) / 10
            }s linear, box-shadow 0.2s`,
            height: '24px',
            width: '24px',
            willChange: 'left',

            ':focus': {
              outline: 'none',
              boxShadow: '0 2px 2px rgba(0, 0, 0, 0.1), 0 0 0 2px #06f',
            },

            [MOBILE]: {
              height: '20px',
              width: '20px',
              top: '-6px',
              transform: 'translateX(-8px)',
            },
          })}
          tabIndex={0}
          role="slider"
          aria-label={intl.formatMessage(messages.label)}
          aria-orientation="horizontal"
          aria-valuemin={0}
          aria-valuemax={duration}
          aria-valuenow={0}
          aria-valuetext={intl.formatMessage(messages.valueText, {
            seconds: 0,
          })}
        ></div>
      </div>
    </div>
  );
};

export default SeekBar;
